import { useCallback, useMemo } from 'react';
import { useSpecificEntityCounts } from '../../core/hooks/entityCounts';
import {
  useDataClient,
  useStudyEntities,
  useStudyMetadata,
} from '../../core/hooks/workspace';
import { useGeoConfig } from '../../core/hooks/geoConfig';
import { plugins } from '../../core/components/computations/plugins';
import { PlotContainerStyleOverrides } from '../../core/components/visualizations/VisualizationTypes';
import { NotebookCellProps } from '../NotebookCell';
import { VisualizationCellDescriptor } from '../Types';
import { useCachedPromise } from '../../core/hooks/cachedPromise';
import ExpandablePanel from '@veupathdb/coreui/lib/components/containers/ExpandablePanel';
import useSnackbar from '@veupathdb/coreui/lib/components/notifications/useSnackbar';
import { NotebookCellPreHeader } from '../NotebookCellPreHeader';
import { NoSubmitOnEnter } from '@veupathdb/components/lib/components/widgets/NoSubmitOnEnter';

export function VisualizationNotebookCell(
  props: NotebookCellProps<VisualizationCellDescriptor>
) {
  const {
    analysisState,
    cell,
    isDisabled,
    expandedPanelState,
    wdkState,
    computeJobStatus,
    stepNumber,
    stepNumbers,
  } = props;
  const { analysis, updateVisualization } = analysisState;
  if (analysis == null) throw new Error('Cannot find analysis.');
  if (wdkState == null) throw new Error('No WDK state.');

  const entities = useStudyEntities();
  const geoConfigs = useGeoConfig(entities);
  const { rootEntity } = useStudyMetadata();
  // The PCA scatterplot and volcano plot notebook visualizations only ever
  // read/gate on the root (sample) entity's count, so fetch just that entity
  // instead of fanning out across every entity in the study via useEntityCounts.
  // This also lets this query share a cache entry (and thus a single backend
  // request) with the root-only counts fetched by ComputeNotebookCell and
  // SubsettingNotebookCell.
  const totalCountsResult = useSpecificEntityCounts(undefined, [rootEntity]);
  const filteredCountsResult = useSpecificEntityCounts(
    analysis.descriptor.subset.descriptor,
    [rootEntity]
  );
  const { enqueueSnackbar } = useSnackbar();

  const { visualizationName, visualizationId, getVizPluginOptions } = cell;

  const { visualization, computation } =
    analysisState.getVisualizationAndComputation(visualizationId) ?? {};
  const computationName = computation?.descriptor.type;

  if (computation == null || computationName == null)
    throw new Error('Cannot find computation.');

  const appPlugin = plugins[computationName];
  const vizPlugin =
    appPlugin && appPlugin.visualizationPlugins[visualizationName];

  // fetch 'apps'
  const dataClient = useDataClient();
  const fetchApps = async () => {
    let { apps } = await dataClient.getApps();
    return { apps };
  };
  const apps = useCachedPromise(fetchApps, ['fetchApps']);
  const appOverview =
    apps && apps.value?.apps.find((app) => app.name === computationName);

  if (appOverview == null)
    throw new Error(
      'Visualizations associated with a computation must have an app overview.'
    );

  const updateConfiguration = useCallback(
    (configuration: unknown) => {
      if (visualization != null) {
        updateVisualization({
          ...visualization,
          descriptor: {
            ...visualization.descriptor,
            configuration,
          },
        });
      }
    },
    [updateVisualization, visualization]
  );

  const vizOverview = appOverview.visualizations.find(
    (v) => v.name === visualizationName
  );
  const constraints = vizOverview?.dataElementConstraints;
  const dataElementDependencyOrder = vizOverview?.dataElementDependencyOrder;

  // Bipartite networks are set to be extra wide, so we need to override
  // that behavior or they'll spill off the screen.
  const plotContainerStyleOverrides: PlotContainerStyleOverrides = {};
  if (visualization?.descriptor.type === 'bipartitenetwork') {
    plotContainerStyleOverrides.width = 1100;
  }

  const vizOptions = useMemo(
    () => ({
      ...vizPlugin?.options,
      ...getVizPluginOptions?.({ wdkState, enqueueSnackbar, stepNumbers }),
    }),
    [
      wdkState,
      enqueueSnackbar,
      getVizPluginOptions,
      vizPlugin?.options,
      stepNumbers,
    ]
  );

  return visualization ? (
    <>
      <NotebookCellPreHeader cell={cell} stepNumber={stepNumber} />
      <NoSubmitOnEnter>
        <ExpandablePanel
          title={cell.title}
          subTitle={''}
          state={expandedPanelState ?? cell.initialPanelState ?? 'open'}
          themeRole="primary"
        >
          <div
            className={'NotebookCellContent' + (isDisabled ? ' disabled' : '')}
          >
            {computation && vizPlugin && (
              <vizPlugin.fullscreenComponent
                options={vizOptions}
                dataElementConstraints={constraints}
                dataElementDependencyOrder={dataElementDependencyOrder}
                visualization={visualization}
                computation={computation}
                computationAppOverview={appOverview}
                filters={analysis.descriptor.subset.descriptor} // issue #1413
                starredVariables={[]} // to be implemented
                toggleStarredVariable={() => {}}
                updateConfiguration={updateConfiguration}
                totalCounts={totalCountsResult}
                filteredCounts={filteredCountsResult}
                geoConfigs={geoConfigs}
                otherVizOverviews={[]}
                computeJobStatus={computeJobStatus}
                hideInputsAndControls={false}
                plotContainerStyleOverrides={plotContainerStyleOverrides}
              />
            )}
          </div>
        </ExpandablePanel>
      </NoSubmitOnEnter>
    </>
  ) : (
    <details>
      <summary>Loading</summary>
    </details>
  );
}
